import React, {Fragment, useState} from "react";
import Typography from "@mui/material/Typography";
import {Button, Chip, CircularProgress, Grid, Paper} from "@mui/material";
import {Link, LoaderFunctionArgs, useLoaderData, useNavigate} from "react-router-dom";
import {MonsterSpeciesList} from "./components/MonsterSpeciesList";
import {MonsterTypeList} from "./components/MonsterTypeList";
import {DamageCarrier, Monster} from "../../server/monster";
import {DataGrid, GridColDef, GridRowSelectionModel, GridToolbarContainer, GridToolbarQuickFilter} from "@mui/x-data-grid";
import AddBoxIcon from '@mui/icons-material/AddBox';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StyleIcon from '@mui/icons-material/Style';
import axios from "axios";
import {MonsterManualBook} from "./components/MonsterManualBook";
import {MonsterIndexCard} from "./components/MonsterIndexCard";

export const monsterListLoader = async ({params}: LoaderFunctionArgs): Promise<Monster[]> => {
    return loadMonsterList();
}

export async function loadMonsterList(): Promise<Monster[]> {
    return axios.get("/monsters/monsters").then(({data}) => data);
}

const monsterColumns: GridColDef[] = [
    {
        field: 'name', headerName: 'Monster', headerAlign: "center", renderCell: (params) => (
            <Link to={'./' + params.row.id}>{params.value}</Link>
        ), minWidth: 300
    },
    {field: 'species', headerName: 'Species', headerAlign: "center", align: "center", valueGetter:(params)=>params.row.type.species.name},
    {field: 'type', headerName: 'Type', headerAlign: "center", valueGetter:(params)=>params.row.type.name},
    {field: 'combat_role', headerName: 'Combat Role', headerAlign: "center", valueGetter:(params)=>params.row.type.combat_role},
    {field: 'ace', headerName: 'ACE', headerAlign: "center", align: "center", valueGetter:(params)=>params.row.attributes?.ace ?? ''},
    {field: 'general_use', headerName: 'Gen Use', headerAlign: "center", valueGetter:(params)=>params.row.type.general_use},
    {
        field: 'dedicated_marshal', headerName: 'Ded. Marshal', headerAlign: "center", align: "center", width: 120,
        renderCell: (params) => params.row.dedicated_marshal ? <Chip label="Ded. Marshal" color="secondary" size="small"/> : ''
    },
]
function MonsterToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarQuickFilter/>
        </GridToolbarContainer>
    )
}

export function MonsterLanding(){
    const [monsters] = useState(useLoaderData() as Monster[])
    const navigate = useNavigate();
    const [printingManual, setPrintingManual] = useState(false);
    const [printingCards, setPrintingCards] = useState(false);
    const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

    const selCount = selectedIds.length;

    async function printMonsterManual() {
        setPrintingManual(true);
        try {
            const [fullMonsters, carriers] = await Promise.all([
                axios.get("/monsters/monsters/full").then(r => r.data),
                axios.get("/monsters/carriers").then(r => r.data),
            ]);

            if (!Array.isArray(fullMonsters)) {
                console.error("Unexpected response from /monsters/monsters/full:", fullMonsters);
                alert("Failed to load monster data. Please restart the server and try again.");
                return;
            }

            // Filter to selection if any rows are checked; otherwise print all
            const monstersToprint: Monster[] = selCount > 0
                ? fullMonsters.filter((m: Monster) => selectedIds.includes(m.id as number))
                : fullMonsters;

            const printedAt = new Date().toLocaleString();
            const win = window.open('', '_blank', 'width=900,height=1100');
            if (!win) return;
            const html = document.createElement('html');
            const head = document.createElement('head');
            head.innerHTML = `<style>
                @page {
                    size: 8.5in 11in;
                    margin: 0.5in;
                    @bottom-left {
                        content: "Printed: ${printedAt}";
                        font-family: Arial, sans-serif;
                        font-size: 9pt;
                        color: #666;
                    }
                    @bottom-center {
                        content: counter(page);
                        font-family: Arial, sans-serif;
                        font-size: 10pt;
                        color: #444;
                    }
                }
                body { margin: 0; padding: 0; background: white; }
                * { box-sizing: border-box; }
            </style>`;
            const body = document.createElement('body');
            const root = document.createElement('div');
            body.appendChild(root);
            html.appendChild(head);
            html.appendChild(body);
            win.document.replaceChild(html, win.document.documentElement);

            import('react-dom/client').then(({createRoot}) => {
                const reactRoot = createRoot(root);
                reactRoot.render(<MonsterManualBook monsters={monstersToprint} carriers={carriers}/>);
                setTimeout(() => {
                    win.focus();
                    win.print();
                }, 800);
            });
        } finally {
            setPrintingManual(false);
        }
    }

    async function printBulkCards() {
        setPrintingCards(true);
        try {
            const [fullMonsters, carriers] = await Promise.all([
                axios.get("/monsters/monsters/full").then(r => r.data),
                axios.get("/monsters/carriers").then(r => r.data),
            ]);

            if (!Array.isArray(fullMonsters)) {
                alert("Failed to load monster data. Please restart the server and try again.");
                return;
            }

            // Filter to selection if any rows are checked; otherwise print all
            const filtered: Monster[] = selCount > 0
                ? fullMonsters.filter((m: Monster) => selectedIds.includes(m.id as number))
                : fullMonsters;

            // Sort alphabetically by monster name
            const sorted = [...filtered].sort((a: Monster, b: Monster) => a.name.localeCompare(b.name));

            const win = window.open('', '_blank', 'width=1100,height=850');
            if (!win) return;
            const html = document.createElement('html');
            const head = document.createElement('head');
            head.innerHTML = `<style>
                @page { size: 11in 8.5in; margin: 0; }
                body { margin: 0; padding: 0; background: white; }
                * { box-sizing: border-box; }
            </style>`;
            const body = document.createElement('body');
            const root = document.createElement('div');
            body.appendChild(root);
            html.appendChild(head);
            html.appendChild(body);
            win.document.replaceChild(html, win.document.documentElement);

            function monsterNeedsDoubleSided(m: Monster): boolean {
                const offCount = (m.offensiveAbilities ?? []).length;
                const defCount = (m.defensiveAbilities ?? []).length;
                const seenIds = new Set<number>();
                const monCount = [
                    ...(m.type?.species?.monstrousAbilities ?? []),
                    ...(m.type?.monstrousAbilities ?? []),
                    ...(m.monstrousAbilities ?? []),
                ].filter(e => {
                    const id = e.ability.id as number;
                    if (seenIds.has(id)) return false;
                    seenIds.add(id);
                    return true;
                }).length;
                return offCount > 6 || defCount > 6 || monCount > 8;
            }

            const gridStyle: React.CSSProperties = {
                display: "grid",
                gridTemplateColumns: "5in 5in",
                gridTemplateRows: "3in 3in",
                gap: "0.25in",
            };
            const cellStyle: React.CSSProperties = {width: "5in", height: "3in", overflow: "hidden"};

            // Each monster gets a sheet of 4 identical cards.
            // Double-sided monsters get a front sheet followed immediately by a back sheet.
            const pages: React.ReactElement[] = [];
            sorted.forEach((m, idx) => {
                const doubleSided = monsterNeedsDoubleSided(m);
                const isLast = idx === sorted.length - 1;
                const frontBreak = doubleSided || !isLast;

                const sheetStyle = (pageBreak: boolean): React.CSSProperties => ({
                    width: "11in", height: "8.5in", overflow: "hidden",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    pageBreakAfter: pageBreak ? "always" : "auto",
                });

                pages.push(
                    <div key={`front-${m.id}`} style={sheetStyle(frontBreak)}>
                        <div style={gridStyle}>
                            {[0, 1, 2, 3].map(i => (
                                <div key={i} style={cellStyle}>
                                    <MonsterIndexCard monster={m} carriers={carriers as DamageCarrier[]} side="front"/>
                                </div>
                            ))}
                        </div>
                    </div>
                );

                if (doubleSided) {
                    pages.push(
                        <div key={`back-${m.id}`} style={sheetStyle(!isLast)}>
                            <div style={gridStyle}>
                                {[0, 1, 2, 3].map(i => (
                                    <div key={i} style={cellStyle}>
                                        <MonsterIndexCard monster={m} carriers={carriers as DamageCarrier[]} side="back"/>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }
            });

            import('react-dom/client').then(({createRoot}) => {
                const reactRoot = createRoot(root);
                reactRoot.render(<>{pages}</>);
                setTimeout(() => {
                    win.focus();
                    win.print();
                }, 800);
            });
        } finally {
            setPrintingCards(false);
        }
    }

    return <Fragment>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    {MonsterSpeciesList()}
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    {MonsterTypeList()}
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" component="h4" align="center">Monsters
                    <Button onClick={() => navigate('./new')}><AddBoxIcon/></Button>
                    <Button
                        onClick={printMonsterManual}
                        disabled={printingManual}
                        startIcon={printingManual ? <CircularProgress size={16}/> : <MenuBookIcon/>}
                        title={selCount > 0 ? `Print manual for ${selCount} selected monster${selCount === 1 ? '' : 's'}` : "Print manual for all monsters"}
                    >
                        {printingManual
                            ? "Loading…"
                            : selCount > 0
                                ? `Print Manual (${selCount})`
                                : "Print Manual (All)"}
                    </Button>
                    <Button
                        onClick={printBulkCards}
                        disabled={printingCards}
                        startIcon={printingCards ? <CircularProgress size={16}/> : <StyleIcon/>}
                        title={selCount > 0 ? `Print cards for ${selCount} selected monster${selCount === 1 ? '' : 's'}` : "Print cards for all monsters"}
                    >
                        {printingCards
                            ? "Loading…"
                            : selCount > 0
                                ? `Print Cards (${selCount})`
                                : "Print Cards (All)"}
                    </Button>
                    </Typography>
                    <DataGrid autoHeight={true}
                              rows={monsters}
                              columns={monsterColumns}
                              getRowId={(row) => row.id}
                              density={"compact"}
                              checkboxSelection
                              rowSelectionModel={selectedIds}
                              onRowSelectionModelChange={setSelectedIds}
                              slots={{toolbar: MonsterToolbar}}
                              initialState={{
                                  pagination: {
                                      paginationModel: {
                                          pageSize: 24,
                                      },
                                  },
                              }}
                    />
                </Paper>
            </Grid>
        </Grid>
    </Fragment>
}